import React from 'react';
import { Box } from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Default color palette
const DEFAULT_COLORS = [
  'rgba(54, 162, 235, 0.8)',
  'rgba(255, 99, 132, 0.8)',
  'rgba(75, 192, 192, 0.8)',
  'rgba(255, 206, 86, 0.8)',
  'rgba(153, 102, 255, 0.8)',
  'rgba(255, 159, 64, 0.8)',
];

const DEFAULT_BORDER_COLORS = [
  'rgba(54, 162, 235, 1)',
  'rgba(255, 99, 132, 1)',
  'rgba(75, 192, 192, 1)',
  'rgba(255, 206, 86, 1)',
  'rgba(153, 102, 255, 1)',
  'rgba(255, 159, 64, 1)',
];

export const BarChartComponent = ({ config }) => {
  const data = {
    labels: config.labels || [],
    datasets: config.datasets || [
      {
        label: config.title || 'Data',
        data: config.data || [],
        backgroundColor: config.colors?.backgroundColor || DEFAULT_COLORS,
        borderColor: config.colors?.borderColor || DEFAULT_BORDER_COLORS,
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: !!config.datasets,
        position: 'top',
      },
      tooltip: {
        enabled: true,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  return (
    <Box sx={{ width: '100%', height: 220 }}>
      <Bar data={data} options={options} />
    </Box>
  );
};

export const LineChartComponent = ({ config }) => {
  const data = {
    labels: config.labels || [],
    datasets: [
      {
        label: config.title || 'Progress',
        data: config.data || [],
        borderColor: config.colors?.borderColor?.[0] || 'rgba(54, 162, 235, 1)',
        backgroundColor: config.colors?.backgroundColor?.[0] || 'rgba(54, 162, 235, 0.2)',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  return (
    <Box sx={{ width: '100%', height: 220 }}>
      <Line data={data} options={options} />
    </Box>
  );
};

export const DonutChartComponent = ({ config }) => {
  const data = {
    labels: config.labels || [],
    datasets: [
      {
        data: config.data || [],
        backgroundColor: config.colors?.backgroundColor || DEFAULT_COLORS,
        borderColor: config.colors?.borderColor || DEFAULT_BORDER_COLORS,
        borderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 10,
          font: {
            size: 12,
          },
        },
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function (context) {
            const label = context.label || '';
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
  };

  return (
    <Box sx={{ width: '100%', height: 220, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <Doughnut data={data} options={options} />
    </Box>
  );
};

export const StackedBarChartComponent = ({ config }) => {
  const data = {
    labels: config.labels || [],
    datasets: config.datasets || [
      {
        label: 'Category 1',
        data: config.data || [],
        backgroundColor: DEFAULT_COLORS[0],
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
      },
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
        beginAtZero: true,
      },
    },
  };

  return (
    <Box sx={{ width: '100%', height: 220 }}>
      <Bar data={data} options={options} />
    </Box>
  );
};
